import { useState, useEffect, useCallback } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { yupResolver } from '@hookform/resolvers/yup'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { RegistrationFormValues } from '@/enums/forms'
import { PageName } from '@/enums/pageName'
import { registrationFieldsKeys } from '@/utils/formsUtils'

import { PrimaryButton } from '@/components/buttons/primaryButton'
import { EmailAutocompleteInput } from '@/components/input'
import { InputErrorMessage } from '@/components/inputErrorMessage'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextHTML } from '@/components/typography'

import { emailKey } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'
import { validationSchema } from '@/validation/registrationFormSchema'

import { TFakeRegistrationPageProps, TRegistrationFormValues } from './types'

export const FakeRegistrationPage = ({
  pageName,
  parameter,
  getItem,
  setItem,
  setLastVisitedItem,
  pageConfig,
  config,
}: TFakeRegistrationPageProps) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const sessionContext = useSession()
  const [isFocus, setFocus] = useState(false)
  const {
    handleSubmit,
    control,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<TRegistrationFormValues>({ resolver: yupResolver(validationSchema), mode: 'all' })
  const email = watch(RegistrationFormValues.email)
  const [emailValue] = getValues(registrationFieldsKeys)
  const {
    title,
    tabTitle,
    pageDescription,
    inputPlaceholder,
    description,
    descriptionImageNode,
    nextUrl,
    iconNode,
    buttonName,
    getEventName,
    replaceParameter,
    showPolicyLink = true,
    showInputIndicator = true,
  } = pageConfig

  const preparedTitle = parameter && replaceParameter ? replaceParameter(parameter, title) : title

  const _pageDescription = pageDescription || (getEventName && getEventName())

  const onSubmit: SubmitHandler<TRegistrationFormValues> = useCallback(
    (data) => {
      if (isSubmitting) {
        return
      }
      const email = data.email
      setItem(emailKey, email)

      setLastVisitedItem(pageName)

      trackPageComplete(
        pageName,
        [],
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )

      navigate(nextUrl)
    },
    [
      isSubmitting,
      navigate,
      setLastVisitedItem,
      pageName,
      nextUrl,
      setItem,
      config,
      sessionContext,
      user,
    ]
  )

  usePageLoadTracking({ pageName, config, user, title: tabTitle || title })

  const handleFocus = async () => {
    setFocus((prevState) => !prevState)
    if (emailValue?.length) {
      await trigger(RegistrationFormValues.email)
    }
  }

  useEffect(() => {
    const email = getItem(emailKey)

    if (!user && !email) {
      return
    }

    setLastVisitedItem(pageName)
    trackPageComplete(
      pageName,
      [],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )

    navigate(nextUrl)
  }, [user, navigate, nextUrl, getItem, setLastVisitedItem, pageName, config, sessionContext])

  const className = `page page-${PageName.registration} page-${pageName}`

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PageTitle title={preparedTitle} />
        {_pageDescription && <TextHTML className="page-description" title={_pageDescription} />}
        <Controller
          name={'email'}
          control={control}
          render={({ field }) => {
            return (
              <EmailAutocompleteInput
                value={field.value || ''}
                onChange={field.onChange}
                isError={Boolean(errors?.email?.message)}
                onFocus={handleFocus}
                onBlur={handleFocus}
                isFocus={isFocus}
                readOnly={isSubmitting}
                disabled={isSubmitting}
                isValid={
                  !Boolean(errors?.email?.message) && isFocus && Boolean(field.value?.length)
                }
                showInputIndicator={showInputIndicator}
                iconNode={iconNode}
                inputPlaceholder={inputPlaceholder}
              />
            )
          }}
        />
        <InputErrorMessage rowNumber={1} message={(!isFocus && errors?.email?.message) || ''} />
        {(description || descriptionImageNode) && (
          <div className="registration-description-container">
            {descriptionImageNode && <div className="icon-container">{descriptionImageNode}</div>}
            {description && (
              <div className="text" dangerouslySetInnerHTML={{ __html: description }}></div>
            )}
          </div>
        )}
        <div className="button-container">
          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !email?.length || Boolean(errors?.email?.message)}
          >
            {buttonName || 'Continue'}
          </PrimaryButton>
        </div>
      </form>
      {showPolicyLink && <FooterPolicy />}
    </div>
  )
}
